import { jSith } from "../jquery-replacement";
import { S25Array } from "./s25.array";
import { S25Promise } from "./s25.promise";
import { isDefined } from "./index";

export namespace S25Async {
    export function throttle(fn: Function, ms?: number, accumulateArgNum?: number) {
        return debounce(fn, ms, false, accumulateArgNum, true, true);
    }

    // Typesafe version of debounce
    // Also does not use $q
    export function debounced<F extends (...params: unknown[]) => unknown>(
        func: F,
        ms: number,
        options?: DebouncedOptions<F>,
    ) {
        const store = new Map<string, DebouncedData<F>>();

        const result = function (...args: Parameters<F>): Promise<ReturnType<F>> {
            const key = options?.hash?.(...args) ?? "none";
            if (!store.has(key)) store.set(key, { lastCall: 0 });
            const data = store.get(key)!;

            if (!data.promise) {
                data.promise = new Promise((res, rej) => {
                    data.resolve = res;
                    data.reject = rej;
                });
            }
            const elapsed = Date.now() - data.lastCall;
            data.lastCall = Date.now();
            if (options?.immediate && elapsed > ms) {
                return Promise.resolve(func.apply(this, args));
            }
            clearTimeout(data.timeout);
            data.pendingArgs = args;
            data.pendingThis = this;
            data.timeout = setTimeout(() => {
                invokePending(data);
            }, ms);
            return data.promise;
        };

        const invokePending = (data: DebouncedData<F>) => {
            if (!data.pendingArgs) return;
            data.resolve!(func.apply(data.pendingThis, data.pendingArgs));
            data.promise = undefined;
            data.pendingArgs = undefined;
            data.pendingThis = undefined;
            data.timeout = undefined;
        };

        result.flush = () => {
            for (const data of store.values()) {
                if (!data.timeout) continue;
                clearTimeout(data.timeout);
                invokePending(data);
            }
        };
        return result;
    }

    // Typesafe version of throttle
    // Also does not use $q
    export function throttled<F extends (...params: any[]) => ReturnType<F>>(
        func: F,
        ms: number,
        debounceLast: boolean = true,
    ) {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        let promise: Promise<ReturnType<F>> | null = null;
        let resolve: (value: ReturnType<F>) => void;
        let reject: (value: any) => void;
        let lastCall: number = 0;
        return function (...args: Parameters<F>): Promise<ReturnType<F>> {
            if (!promise) {
                promise = new Promise((res, rej) => {
                    resolve = res;
                    reject = rej;
                });
            }
            clearTimeout(timeout);
            const elapsed = Date.now() - lastCall;
            if (ms - elapsed <= 0) {
                lastCall = Date.now();
                return Promise.resolve(func.apply(this, args));
            }
            if (!debounceLast) return promise; // Drop call if it's not time
            timeout = setTimeout(() => {
                lastCall = Date.now();
                resolve(func.apply(this, args));
                promise = null;
            }, ms - elapsed);
            return promise;
        };
    }

    //only run 1 fn call every ms milliseconds no matter how many come in -- if another comes in too fast, we clear the last one and start over with the new one, and so on
    //returns: function that returns promise
    export function debounceGenerator(
        deferFunc: Function,
        timeoutFunc: Function,
        clearFunc: Function,
        fn: Function,
        ms?: number,
        rejectOnClear?: boolean,
        accumulateArgNum?: number,
        resolveOnFinal?: boolean,
        throttle?: boolean,
    ) {
        ms = ms || 250;

        let timeout: any,
            defer = deferFunc(),
            accumulator: any[] = [],
            counter = 0;

        //return wrapper function that wraps debounce functionality around fn and can be called the exact same way as the original fn
        return function (...argz: any): any {
            //note: arguments variable is implied is js so no need to actually bind variables here...
            const args = argz; //keep reference to variables passed in (even though none are formally bound above) so we can apply them to fn after ms milliseconds...

            //push arguments for each call into accumulator, if accumulateArgNum provided
            if (isDefined(accumulateArgNum) && accumulateArgNum > -1 && args[accumulateArgNum]?.length) {
                Array.prototype.push.apply(accumulator, args[accumulateArgNum]);
            }

            if (timeout) {
                //implies defer is also set
                //clear timeout so it never executes (ie, last call never executes)
                //we clear bc it means another request has come in *before* the last one has waited ms milliseconds
                //note: if throttling (NOT debouncing): we execute once every X ms like an interval, so don't cancel; instead, return
                !throttle && clearFunc(timeout);

                if (!resolveOnFinal) {
                    if (rejectOnClear) {
                        defer.reject("debounce timeout cleared"); //reject any promise attached to last call
                    } else {
                        defer.resolve("debounce timeout cleared"); //silently resolve it but with a message indicating the clear (angular logs uncaught rejection if reject called w/ no handler...)
                    }
                }

                if (throttle) {
                    return defer.promise;
                }
            }

            if (!resolveOnFinal) {
                //set defer so we can return a promise
                defer = deferFunc();
            }

            //local ref to defer so any other callers do not change it for this call
            const myDefer = defer;

            counter++;
            const closureCounter = counter;

            //set timeout for execution -- it will be cleared (and promise rejected) if another call happens before timeout period (ms)
            timeout = timeoutFunc(function () {
                if (isDefined(accumulateArgNum) && accumulateArgNum > -1) {
                    accumulator = S25Array.uniqueDeep(accumulator); //unique accumulated param
                    args[accumulateArgNum] = Array.prototype.splice.apply(accumulator, [0]); //set param to all unique entries
                }
                const resp: any = fn.apply(null, args); //no calls w/ in timeout period, so run function with arguments
                timeout = null; //nullify timeout
                defer = deferFunc();
                if (resp && resp.then) {
                    //if function returns promise...
                    resp.then(function (data: any) {
                        //then return the resolved defer with the function's data response
                        if (resolveOnFinal) {
                            //resolve all calls at once, obvs order does not matter; reset defer for another round
                            myDefer.resolve(data);
                        } else if (counter === closureCounter) {
                            //todo: only care if params are the same and caller cares about order (specific occ avail checking...)
                            myDefer.resolve(data);
                        }
                    });
                } else {
                    myDefer.resolve(resp); //resolve with literal response
                    if (resolveOnFinal) {
                        defer = deferFunc();
                    }
                }
            }, ms);
            return myDefer.promise; //return deferred promise
        };
    }

    export function debounce(
        fn: Function,
        ms?: number,
        rejectOnClear?: boolean,
        accumulateArgNum?: number,
        resolveOnFinal?: boolean,
        throttle?: boolean,
    ) {
        return debounceGenerator(
            window.angBridge?.$q?.defer,
            window.angBridge?.$timeout,
            window.angBridge?.$timeout?.cancel,
            fn,
            ms,
            rejectOnClear,
            accumulateArgNum,
            resolveOnFinal,
            throttle,
        );
    }

    export function debounceTs(
        fn: Function,
        ms?: number,
        rejectOnClear?: boolean,
        accumulateArgNum?: number,
        resolveOnFinal?: boolean,
        throttle?: boolean,
    ): (...args: any[]) => any;
    export function debounceTs(options: DebounceTsOptions): (...args: any[]) => any;
    export function debounceTs(
        fnOrOptions: Function | DebounceTsOptions,
        ms?: number,
        rejectOnClear?: boolean,
        accumulateArgNum?: number,
        resolveOnFinal?: boolean,
        throttle?: boolean,
    ) {
        if (typeof fnOrOptions !== "function") {
            const {
                fn,
                ms: delayMs,
                rejectOnClear: rejectOnClearOption,
                accumulateArgNum: accumulateArgNumOption,
                resolveOnFinal: resolveOnFinalOption,
                throttle: throttleOption,
            } = fnOrOptions;
            return debounceTs(
                fn,
                delayMs,
                rejectOnClearOption,
                accumulateArgNumOption,
                resolveOnFinalOption,
                throttleOption,
            );
        }

        return debounceGenerator(
            jSith.defer,
            setTimeout,
            clearTimeout,
            fnOrOptions,
            ms,
            rejectOnClear,
            accumulateArgNum,
            resolveOnFinal,
            throttle,
        );
    }

    // Momentarily changes the content of the for loop such that it has to be re-rendered
    export async function refreshNgFor(obj: any, property: string) {
        const data = obj[property];
        obj[property] = [];
        await S25Promise.delay(0);
        obj[property] = data;
    }

    // Returns a function to refresh a specific loop in the template
    export function refreshableNgFor(obj: any, property: string) {
        return () => refreshNgFor(obj, property);
    }

    export type DebounceTsOptions = {
        fn: Function;
        ms?: number;
        rejectOnClear?: boolean;
        accumulateArgNum?: number;
        resolveOnFinal?: boolean;
        throttle?: boolean;
    };

    export type DebouncedOptions<F extends (...args: unknown[]) => unknown> = {
        immediate?: boolean; // Trigger right away if it's been more than "ms" ms since last call
        hash?: (...args: Parameters<F>) => string; // Debounce each hash individually
    };

    export type DebouncedData<F extends (...args: unknown[]) => unknown> = {
        timeout?: ReturnType<typeof setTimeout>;
        promise?: Promise<ReturnType<F>>;
        resolve?: (value: ReturnType<F>) => void;
        reject?: (value: any) => void;
        lastCall: number;
        pendingArgs?: Parameters<F>;
        pendingThis?: unknown;
    };
}
